import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFormSecondaryAction,
  AdminHeaderAction,
  AdminLinkAction,
  AdminMainText,
  AdminMetadataText,
  AdminPanelTitle,
  AdminRowAction,
  AdminRowActionsButton,
  AdminRowMenuIcon,
  AdminRowMenuItem,
  AdminSupportingText,
  AdminTabButton,
  adminFormTextareaClasses,
  adminWizardSelectClasses,
  ADMIN_TAB_CONTENT_WIDE_CLASS,
  adminDetailTabContentClassName,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
} from '../shared/adminUx';
import { POPUP_FORM_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import useClickOutside from '../../../Hooks/useClickOutside';
import {
  APPEAL_HANDLER_OPTIONS,
  displayHandlerName,
  getActionOwnerLabel,
  getActionOwnerPath,
  getAppliedRuleSnapshot,
  getCorrectiveActionLabel,
  getOriginalDecisionPath,
  getSourceRecordPath,
} from '../../../data/appealsCasesData';
import {
  ADMIN_OPENED_CASE_KEYS,
  openCaseAndAdvanceStatus,
} from '../../../utils/adminOpenedCases';
import { recordCaseActivity } from '../../../data/changeHistoryAudit';

const REPLY_MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const REPLY_ALLOWED_EXTENSIONS = [
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.webp',
  '.doc',
  '.docx',
  '.txt',
];

const SAMPLE_PDF_PREVIEW_URL =
  'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf';

const NOTICE_TIME_ZONE = 'Australia/Sydney';

const formatDateTime = (date, { withZone = false } = {}) => {
  if (!date) return '—';
  const d = date instanceof Date ? date : new Date(date);
  const datePart = d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const base = `${datePart}, ${timePart}`;
  return withZone ? `${base}, ${NOTICE_TIME_ZONE}` : base;
};

const nextMessageId = (messages = []) => {
  const maxNum = messages.reduce((max, m) => {
    const match = String(m.messageId || '').match(/(\d+)/);
    const n = match ? parseInt(match[1], 10) : 0;
    return Math.max(max, n);
  }, 900);
  return `AMSG-${String(maxNum + 1).padStart(6, '0')}`;
};

function getAttachmentPreviewKind(att, resolvedUrl) {
  const name = (att?.name || '').toLowerCase();
  const type = (att?.type || '').toLowerCase();
  const u = (resolvedUrl || '').toLowerCase();
  if (name.endsWith('.pdf') || type === 'pdf' || /\.pdf(\?|$)/i.test(u)) return 'pdf';
  if (
    /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name) ||
    ['png', 'jpeg', 'jpg', 'gif', 'webp'].some((x) => type === x || type.includes('image')) ||
    /picsum\.photos|\.(png|jpe?g|gif|webp)(\?|$)/i.test(u)
  ) {
    return 'image';
  }
  return 'other';
}

function resolveAttachmentDisplayUrl(att) {
  const raw = att?.url;
  if (raw && raw !== '#' && /^https?:\/\//i.test(String(raw).trim())) {
    return String(raw).trim();
  }
  const kindByName = getAttachmentPreviewKind(att, '');
  if (kindByName === 'pdf') return SAMPLE_PDF_PREVIEW_URL;
  if (kindByName === 'image') {
    const seed = encodeURIComponent((att?.name || 'file').replace(/[^a-z0-9]/gi, '').slice(0, 40) || 'file');
    return `https://picsum.photos/seed/tp-${seed}/1200/800`;
  }
  return null;
}

const buildAppealDetail = (appealFromState, appealIdParam) => {
  const id = appealFromState?.appealId || appealIdParam || 'APL-000104';
  const source =
    appealFromState?.source || appealFromState?.sourceModule || 'Moderation';

  const base = {
    appealId: id,
    originalDecisionId: appealFromState?.originalDecisionId || 'MOD-DEC-0785',
    submittedByName: appealFromState?.submittedByName || 'Harbour Plumbing',
    accountId: appealFromState?.accountId || 'BUS-001284',
    accountType: appealFromState?.accountType || 'Business',
    source,
    sourceModule: source,
    decisionType: appealFromState?.decisionType || 'Post — Confirmed',
    assignedHandler: appealFromState?.assignedHandler ?? 'Alex Morgan',
    status: appealFromState?.status || 'In Progress',
    submittedOn: appealFromState?.submittedOn
      ? new Date(appealFromState.submittedOn)
      : new Date('2026-08-08T10:15:00'),
    lastUpdated: appealFromState?.lastUpdated
      ? new Date(appealFromState.lastUpdated)
      : new Date('2026-08-08T13:40:00'),
    noticeAvailableOn: new Date('2026-08-04T09:30:00'),
    appealDeadline: new Date('2026-08-11T09:30:00'),
    decisionReason:
      'The post image was assessed as unrelated to the advertised plumbing service.',
    decisionDateTime: new Date('2026-08-04T09:30:00'),
    originalDecisionMakerName: 'Sara Khan',
    affectedRecordId: 'PST-000785',
    affectedRecordTitle: 'Emergency Hot-Water Repairs',
    affectedRecordKind: 'Post',
    originalNotification: 'Dashboard notification and email',
    originalAction: 'Post unpublished and moved to Draft.',
    requiredCorrection: 'Replace the affected image before republishing.',
    appealReason:
      'The image shows the completed hot-water installation described in the post.',
    initialMessageId: 'AMSG-000901',
    initialAttachmentName: 'hot-water-installation-original.jpg',
    relatedModerationCaseId: 'MOD-002411',
    relatedFraudCaseId: null,
    currentOutcome: 'Pending',
    decisionHistory: [],
    actionResult: null,
    actionUnfinished: false,
    accountAvatar: appealFromState?.accountAvatar || null,
    noticeTimeZone: NOTICE_TIME_ZONE,
  };

  if (source === 'Moderation' && String(base.decisionType).includes('Review')) {
    base.affectedRecordId = 'REV-000412';
    base.affectedRecordTitle = 'Customer review for Harbour Plumbing';
    base.affectedRecordKind = 'Review';
    base.decisionReason = 'The review was assessed as containing prohibited content.';
    base.relatedModerationCaseId = 'MOD-002520';
    base.originalAction = 'Review unpublished and removed from public display.';
    base.requiredCorrection = '—';
    base.originalDecisionId = appealFromState?.originalDecisionId || 'MOD-DEC-2411';
  } else if (source === 'Compliance') {
    base.affectedRecordId = 'LIC-001498';
    base.affectedRecordTitle = 'Electrical Licence';
    base.affectedRecordKind = 'Licence';
    base.decisionReason = 'Submitted licence documentation did not meet verification requirements.';
    base.relatedModerationCaseId = null;
    base.originalAction = 'Licence application marked Rejected.';
    base.requiredCorrection = 'Resubmit a clear licence document.';
    base.originalDecisionId = appealFromState?.originalDecisionId || 'CMP-DEC-1498';
  } else if (source === 'Fraud & Security') {
    base.affectedRecordId = base.accountId;
    base.affectedRecordTitle = base.submittedByName;
    base.affectedRecordKind = 'Account';
    base.decisionReason =
      base.decisionType === 'Permanent Ban'
        ? 'Account activity met the criteria for a permanent ban.'
        : 'Account activity met the criteria for a final warning.';
    base.relatedFraudCaseId = 'FRD-000318';
    base.relatedModerationCaseId = 'FRD-000318';
    base.originalAction =
      base.decisionType === 'Permanent Ban'
        ? 'Account permanently banned.'
        : 'Final warning issued against the account.';
    base.requiredCorrection = '—';
    base.originalDecisionId = appealFromState?.originalDecisionId || 'DEC-FS-002105';
  }

  const appliedRule = getAppliedRuleSnapshot(base);
  base.appliedRule = appliedRule;
  base.appliedRuleId = appliedRule.ruleId;
  base.correctiveActionLabel = getCorrectiveActionLabel(base);
  base.actionOwner = getActionOwnerLabel(base);

  const isDecided = base.status === 'Decided';
  const isWithdrawn = base.status === 'Withdrawn';

  const messages =
    base.status === 'New'
      ? [
          {
            messageId: 'AMSG-000901',
            senderName: base.submittedByName,
            senderType: base.accountType,
            isHandler: false,
            message: base.appealReason,
            timestamp: base.submittedOn,
            readStatus: 'Read',
            attachments: base.initialAttachmentName
              ? [{ id: 'att1', name: base.initialAttachmentName, type: 'JPG', size: '428 KB' }]
              : [],
          },
        ]
      : [
          {
            messageId: 'AMSG-000901',
            senderName: base.submittedByName,
            senderType: base.accountType,
            isHandler: false,
            message:
              'The image shows the completed hot-water installation described in the post. I have attached the original image for review.',
            timestamp: base.submittedOn,
            readStatus: 'Read',
            attachments: [
              {
                id: 'att1',
                name: base.initialAttachmentName || 'hot-water-installation-original.jpg',
                type: 'JPG',
                size: '428 KB',
              },
            ],
          },
          {
            messageId: 'AMSG-000902',
            senderName: base.assignedHandler || 'Alex Morgan',
            senderType: 'Support Team Member',
            isHandler: true,
            message: `Please confirm that the attached image was taken at the job shown in Post ${base.affectedRecordId}.`,
            timestamp: new Date('2026-08-08T11:40:00'),
            readStatus: 'Read',
            attachments: [],
          },
          {
            messageId: 'AMSG-000903',
            senderName: base.submittedByName,
            senderType: base.accountType,
            isHandler: false,
            message:
              'Confirmed. The image was taken at the completed installation shown in the post.',
            timestamp: base.lastUpdated,
            readStatus: 'Read',
            attachments: [
              { id: 'att2', name: 'customer-job-confirmation.pdf', type: 'PDF', size: '186 KB' },
            ],
          },
        ];

  if (isDecided) {
    base.currentOutcome = 'Appeal Approved';
    base.actionResult = 'Post restored';
    base.actionUnfinished = false;
    base.decisionHistory = [
      {
        version: 1,
        appealOutcome: 'Appeal Approved',
        decisionReason:
          'The additional evidence confirms that the image relates to the service and job described in the affected post.',
        correctiveAction: base.correctiveActionLabel,
        correctiveActionOwner: base.actionOwner,
        actionResult: 'Post restored',
        decidedBy: base.assignedHandler || 'Alex Morgan',
        decidedOn: base.lastUpdated,
        decisionState: 'Current',
        notificationStatus: 'Generated',
        internalDecisionNote: 'Original image and customer confirmation reviewed.',
        correctionReason: null,
      },
    ];
  }

  if (isWithdrawn) {
    base.currentOutcome = '—';
  }

  return {
    ...base,
    messages,
    latestActivity:
      messages.length > 1
        ? `${base.accountType} supplied the requested confirmation.`
        : 'Appeal submitted.',
    messageCount: messages.length,
    attachmentCount: messages.reduce((n, m) => n + (m.attachments?.length || 0), 0),
  };
};

const AppealDetail = () => {
  const { appealId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const appealFromState = location.state?.appeal;

  const [activeTab, setActiveTab] = useState('overview');
  const [appeal, setAppeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState(null);

  const [replyText, setReplyText] = useState('');
  const [replyFile, setReplyFile] = useState(null);
  const [replyError, setReplyError] = useState('');
  const [replyAttachmentError, setReplyAttachmentError] = useState('');
  const [composerMenuOpen, setComposerMenuOpen] = useState(false);
  const replyComposerRef = useRef(null);
  const replyFileInputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const composerMenuRef = useRef(null);

  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [reassignSelection, setReassignSelection] = useState(APPEAL_HANDLER_OPTIONS[0]);
  const [attachmentPreview, setAttachmentPreview] = useState(null);

  useClickOutside(composerMenuRef, () => setComposerMenuOpen(false));

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await new Promise((r) => setTimeout(r, 300));
      const detail = buildAppealDetail(appealFromState, appealId);
      const opened = openCaseAndAdvanceStatus(
        ADMIN_OPENED_CASE_KEYS.appeals,
        detail,
        'appealId'
      );
      setAppeal(
        opened.status !== detail.status
          ? {
              ...opened,
              latestActivity: 'Status changed: New → In Progress.',
            }
          : opened
      );
      setLoading(false);
    };
    load();
  }, [appealId, appealFromState]);

  useEffect(() => {
    if (!attachmentPreview) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setAttachmentPreview(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [attachmentPreview]);

  const openAttachmentPreview = (att) => {
    const url = resolveAttachmentDisplayUrl(att);
    const kind = url ? getAttachmentPreviewKind(att, url) : getAttachmentPreviewKind(att, '');
    setAttachmentPreview({ attachment: att, url, kind });
  };

  const messagesChronological = useMemo(() => {
    if (!appeal?.messages) return [];
    return [...appeal.messages].sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return ta - tb;
    });
  }, [appeal?.messages]);

  useEffect(() => {
    if (activeTab !== 'messages') return undefined;
    const timer = setTimeout(() => {
      const container = messagesContainerRef.current;
      if (container) container.scrollTop = container.scrollHeight;
    }, 200);
    return () => clearTimeout(timer);
  }, [activeTab, messagesChronological.length]);

  const canMessage =
    Boolean(appeal) &&
    (appeal.status === 'New' ||
      appeal.status === 'In Progress' ||
      appeal.status === 'Awaiting Reply');

  const isDecided = appeal?.status === 'Decided';
  const isWithdrawn = appeal?.status === 'Withdrawn';
  const isReadOnlyMessages = isDecided || isWithdrawn;
  const isUnassigned = !appeal?.assignedHandler;

  const accountPath = !appeal
    ? '/admin/dashboard'
    : appeal.accountType === 'Customer'
      ? `/admin/users/customers/${appeal.accountId}`
      : `/admin/users/businesses/${appeal.accountId}`;

  const originalDecisionPath = appeal ? getOriginalDecisionPath(appeal) : '/admin/dashboard';
  const sourceRecordPath = appeal ? getSourceRecordPath(appeal) : '/admin/dashboard';
  const actionOwnerPath = appeal ? getActionOwnerPath(appeal) : '/admin/dashboard';

  const affectedRecordPath = (() => {
    if (!appeal) return null;
    if (appeal.affectedRecordKind === 'Post') return `/admin/posts/${appeal.affectedRecordId}`;
    if (appeal.affectedRecordKind === 'Review') return `/admin/reviews/${appeal.affectedRecordId}`;
    if (appeal.affectedRecordKind === 'Licence') {
      return `/admin/licences-badges-trade-tickets/${appeal.affectedRecordId}`;
    }
    return accountPath;
  })();

  const handlerPath = appeal?.assignedHandler
    ? `/admin/settings/team-members/${encodeURIComponent(
        appeal.assignedHandler.replace(/\s+/g, '-')
      )}`
    : null;

  const currentDecision = (appeal?.decisionHistory || []).find((d) => d.decisionState === 'Current');

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText className="text-offblack">Loading appeal details...</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  if (!appeal) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AdminAlertText className="mb-4">Appeal not found</AdminAlertText>
            <AdminHeaderAction onClick={() => navigate('/admin/appeals')}>
              Back to Appeals
            </AdminHeaderAction>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const handleStartReview = () => {
    if (appeal.status !== 'New') return;
    if (!appeal.assignedHandler) return;
    const now = new Date();
    const appealRef = appeal.appealId || appeal.id;
    setAppeal((prev) => ({
      ...prev,
      status: 'In Progress',
      lastUpdated: now,
      latestActivity: 'Review started.',
    }));
    if (appealRef) {
      try {
        recordCaseActivity({
          caseRef: appealRef,
          module: 'Appeals',
          sourceModuleRef: 'appeals',
          changeType: 'Review started',
          activityDetails: `${appeal.assignedHandler}; New → In Progress`,
          changedBy: appeal.assignedHandler,
          relatedRecord: appealRef,
        });
      } catch {
        /* history recording failure must not reverse the source save */
      }
    }
  };

  const openReassignModal = () => {
    setReassignSelection(
      APPEAL_HANDLER_OPTIONS.includes(appeal.assignedHandler)
        ? appeal.assignedHandler
        : APPEAL_HANDLER_OPTIONS[0]
    );
    setReassignModalOpen(true);
  };

  const saveReassign = () => {
    if (!reassignSelection) return;
    if (reassignSelection === appeal.assignedHandler) {
      setReassignModalOpen(false);
      return;
    }
    const now = new Date();
    const previousHandler = appeal.assignedHandler;
    const appealRef = appeal.appealId || appeal.id;
    setAppeal((prev) => ({
      ...prev,
      assignedHandler: reassignSelection,
      lastUpdated: now,
      latestActivity: `Assigned to ${reassignSelection}.`,
    }));
    setReassignModalOpen(false);
    if (appealRef) {
      try {
        recordCaseActivity({
          caseRef: appealRef,
          module: 'Appeals',
          sourceModuleRef: 'appeals',
          changeType: 'Reassigned',
          activityDetails: `${previousHandler || 'Unassigned'} → ${reassignSelection}`,
          changedBy: reassignSelection,
          relatedRecord: appealRef,
        });
      } catch {
        /* history recording failure must not reverse the source save */
      }
    }
  };

  const handleReplyTextChange = (e) => {
    setReplyText(e.target.value);
    if (replyError && e.target.value.trim()) setReplyError('');
  };

  const handleReplyFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setReplyFile(null);
      setReplyAttachmentError('');
      return;
    }
    const name = file.name.toLowerCase();
    const hasAllowedExt = REPLY_ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext));
    if (!hasAllowedExt) {
      setReplyFile(null);
      setReplyAttachmentError('This file type is not allowed.');
      e.target.value = '';
      return;
    }
    if (file.size > REPLY_MAX_ATTACHMENT_BYTES) {
      setReplyFile(null);
      setReplyAttachmentError('Attachment must be 10 MB or smaller.');
      e.target.value = '';
      return;
    }
    setReplyFile(file);
    setReplyAttachmentError('');
  };

  const handleSendMessage = () => {
    const text = replyText.trim();
    let hasError = false;
    if (!text) {
      setReplyError('Message is required.');
      hasError = true;
    } else setReplyError('');
    if (replyAttachmentError) hasError = true;
    if (hasError) return;
    if (isUnassigned) {
      setReplyError('Assign an eligible handler first.');
      return;
    }

    const now = new Date();
    const attachments = replyFile
      ? [
          {
            id: `att-${now.getTime()}`,
            name: replyFile.name,
            type: (replyFile.type && replyFile.type.split('/').pop()?.toUpperCase()) || 'File',
            url: URL.createObjectURL(replyFile),
          },
        ]
      : [];

    const newMessage = {
      messageId: nextMessageId(appeal.messages),
      senderName: appeal.assignedHandler,
      senderType: 'Support Team Member',
      isHandler: true,
      message: text,
      timestamp: now,
      readStatus: 'Unread',
      attachments,
    };

    const appealRef = appeal.appealId || appeal.id;
    setAppeal((prev) => ({
      ...prev,
      messages: [...(prev.messages || []), newMessage],
      lastUpdated: now,
      messageCount: (prev.messageCount || 0) + 1,
      attachmentCount: (prev.attachmentCount || 0) + attachments.length,
      latestActivity: 'Handler message sent.',
    }));
    if (appealRef) {
      try {
        recordCaseActivity({
          caseRef: appealRef,
          module: 'Appeals',
          sourceModuleRef: 'appeals',
          changeType: 'Message sent',
          activityDetails: `${appeal.assignedHandler}; handler message sent`,
          changedBy: appeal.assignedHandler,
          relatedRecord: appealRef,
        });
      } catch {
        /* history recording failure must not reverse the source save */
      }
    }
    setReplyText('');
    setReplyFile(null);
    setReplyError('');
    setReplyAttachmentError('');
    setComposerMenuOpen(false);
    if (replyFileInputRef.current) replyFileInputRef.current.value = '';
  };

  const renderMessageAttachments = (attachments, isHandler = false) => {
    if (!attachments?.length) return null;
    const textClass = isHandler ? 'text-white' : 'text-offblack';
    const metaClass = isHandler ? 'text-white/80' : 'text-mediumgray';
    return (
      <div className="mt-2 space-y-1 min-w-0">
        {attachments.map((att) => (
          <button
            key={att.id || att.name}
            type="button"
            onClick={() => openAttachmentPreview(att)}
            title={att.name}
            className="flex items-center gap-2 w-full min-w-0 bg-transparent border-0 p-0 cursor-pointer text-left"
          >
            <i className={`fi fi-rs-file flex-shrink-0 text-sm ${textClass}`} aria-hidden />
            <span className="min-w-0 flex-1 overflow-hidden">
              <AdminMainText className={`${textClass} block truncate`}>{att.name}</AdminMainText>
              {(att.type || att.size) && (
                <AdminMetadataText className={`${metaClass} block truncate`}>
                  {att.type ? `${att.type}${att.size ? ` · ${att.size}` : ''}` : att.size}
                </AdminMetadataText>
              )}
            </span>
          </button>
        ))}
      </div>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'messages', label: 'Appeal Messages' },
    { id: 'decision', label: 'Decision' },
  ];

  const canReassign = !isWithdrawn;
  const canStartReview = appeal.status === 'New' && Boolean(appeal.assignedHandler);
  const replySendDisabled = !replyText.trim() || Boolean(replyAttachmentError) || isUnassigned;
  const handlerDisplay = displayHandlerName(appeal.assignedHandler);

  const handleCopyField = async (fieldKey, value) => {
    if (!value || value === '—') return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedField(fieldKey);
      window.setTimeout(() => setCopiedField(null), 1500);
    } catch {
      // ignore clipboard errors
    }
  };

  const renderCopyButton = (fieldKey, value, label) => {
    const canCopy = Boolean(value && value !== '—');
    if (!canCopy) return null;
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          handleCopyField(fieldKey, value);
        }}
        className="inline-flex items-center justify-center text-mediumgray hover:text-primaryblue shrink-0"
        title={copiedField === fieldKey ? 'Copied' : `Copy ${label}`}
        aria-label={copiedField === fieldKey ? 'Copied' : `Copy ${label}`}
      >
        <i
          className={`fi ${
            copiedField === fieldKey ? 'fi-rr-check text-freshgreen' : 'fi-rr-copy'
          } text-xs leading-none`}
          aria-hidden
        />
      </button>
    );
  };

  const renderCopyableValue = (fieldKey, value, label, content) => {
    const display = value || '—';
    return (
      <AdminDetailFieldValue as="div" className="inline-flex items-center gap-1.5 min-w-0">
        {content ?? (
          <span className="truncate" title={display}>
            {display}
          </span>
        )}
        {renderCopyButton(fieldKey, value, label)}
      </AdminDetailFieldValue>
    );
  };

  const renderField = (label, valueNode) => (
    <div className="space-y-1">
      <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
      {valueNode}
    </div>
  );

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>
          {/* Screen 2 header — title only (metadata lives in Overview) */}
          <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
            <AdminPanelTitle className="text-offblack">
              {appeal.appealId} — Appeal
            </AdminPanelTitle>
          </DashboardMainContainer>

          <DashboardMainContainer className="!bg-white">
            <div className="border-b border-lightgray">
              <div className="flex space-x-8 overflow-x-auto">
                {tabs.map((tab) => (
                  <AdminTabButton
                    key={tab.id}
                    active={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </AdminTabButton>
                ))}
              </div>
            </div>

            <div
              className={
                activeTab === 'messages'
                  ? ADMIN_TAB_CONTENT_WIDE_CLASS
                  : adminDetailTabContentClassName(activeTab)
              }
            >
              {activeTab === 'overview' && (
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Column 1 — appeal identity */}
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Appeal ID</AdminDetailFieldLabel>
                        {renderCopyableValue('appealId', appeal.appealId, 'Appeal ID')}
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Original ID</AdminDetailFieldLabel>
                        {renderCopyableValue(
                          'originalDecisionId',
                          appeal.originalDecisionId,
                          'Original ID',
                          <AdminLinkAction
                            type="button"
                            className="text-left"
                            onClick={() => navigate(originalDecisionPath)}
                          >
                            {appeal.originalDecisionId || '—'}
                          </AdminLinkAction>
                        )}
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Status</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>{appeal.status || '—'}</AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Submitted By</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          <AdminLinkAction
                            type="button"
                            className="text-left"
                            onClick={() => navigate(accountPath)}
                          >
                            {appeal.submittedByName || '—'}
                          </AdminLinkAction>
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Account ID</AdminDetailFieldLabel>
                        {renderCopyableValue(
                          'accountId',
                          appeal.accountId,
                          'Account ID',
                          <AdminLinkAction
                            type="button"
                            className="text-left"
                            onClick={() => navigate(accountPath)}
                          >
                            {appeal.accountId || '—'}
                          </AdminLinkAction>
                        )}
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Account Type</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>{appeal.accountType || '—'}</AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Source</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          <AdminLinkAction
                            type="button"
                            className="text-left"
                            onClick={() => navigate(sourceRecordPath)}
                          >
                            {appeal.source || '—'}
                          </AdminLinkAction>
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Decision Type</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          <AdminLinkAction
                            type="button"
                            className="text-left"
                            onClick={() => navigate(originalDecisionPath)}
                          >
                            {appeal.decisionType || '—'}
                          </AdminLinkAction>
                        </AdminDetailFieldValue>
                      </div>
                    </div>

                    {/* Column 2 — original decision */}
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Decision Reason</AdminDetailFieldLabel>
                        <AdminDetailFieldValue className="whitespace-pre-wrap">
                          {appeal.decisionReason || '—'}
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Decided On</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          {formatDateTime(appeal.decisionDateTime)}
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Decided By</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          <AdminLinkAction
                            type="button"
                            className="text-left"
                            onClick={() =>
                              navigate(
                                `/admin/settings/team-members/${encodeURIComponent(
                                  (appeal.originalDecisionMakerName || 'Sara Khan').replace(
                                    /\s+/g,
                                    '-'
                                  )
                                )}`
                              )
                            }
                          >
                            {appeal.originalDecisionMakerName || '—'}
                          </AdminLinkAction>
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Affected Record</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          {affectedRecordPath ? (
                            <AdminLinkAction
                              type="button"
                              className="text-left"
                              onClick={() => navigate(affectedRecordPath)}
                            >
                              {appeal.affectedRecordId} — {appeal.affectedRecordTitle}
                            </AdminLinkAction>
                          ) : (
                            `${appeal.affectedRecordId || '—'} — ${
                              appeal.affectedRecordTitle || '—'
                            }`
                          )}
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Original Notice</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          {appeal.originalNotification || '—'}
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Notice Available</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          {formatDateTime(appeal.noticeAvailableOn, { withZone: true })}
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Original Action</AdminDetailFieldLabel>
                        <AdminDetailFieldValue className="whitespace-pre-wrap">
                          {appeal.originalAction || '—'}
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Required Correction</AdminDetailFieldLabel>
                        <AdminDetailFieldValue className="whitespace-pre-wrap">
                          {appeal.requiredCorrection || '—'}
                        </AdminDetailFieldValue>
                      </div>
                    </div>

                    {/* Column 3 — appeal summary */}
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Appeal Reason</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          <AdminLinkAction
                            type="button"
                            className="text-left"
                            onClick={() => setActiveTab('messages')}
                          >
                            {appeal.appealReason || '—'}
                          </AdminLinkAction>
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Initial Message</AdminDetailFieldLabel>
                        {renderCopyableValue(
                          'initialMessageId',
                          appeal.initialMessageId,
                          'Initial Message',
                          <AdminLinkAction
                            type="button"
                            className="text-left"
                            onClick={() => setActiveTab('messages')}
                          >
                            {appeal.initialMessageId || '—'}
                          </AdminLinkAction>
                        )}
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Initial Attachment</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          {appeal.initialAttachmentName ? (
                            <AdminLinkAction
                              type="button"
                              className="text-left"
                              onClick={() =>
                                openAttachmentPreview({
                                  id: 'initial-att',
                                  name: appeal.initialAttachmentName,
                                  type: 'JPG',
                                })
                              }
                            >
                              {appeal.initialAttachmentName}
                            </AdminLinkAction>
                          ) : (
                            '—'
                          )}
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Messages</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          <AdminLinkAction
                            type="button"
                            className="text-left"
                            onClick={() => setActiveTab('messages')}
                          >
                            {appeal.messageCount ?? '—'}
                          </AdminLinkAction>
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Attachments</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          <AdminLinkAction
                            type="button"
                            className="text-left"
                            onClick={() => setActiveTab('messages')}
                          >
                            {appeal.attachmentCount ?? '—'}
                          </AdminLinkAction>
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Latest Activity</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          <AdminLinkAction
                            type="button"
                            className="text-left"
                            onClick={() => setActiveTab('messages')}
                          >
                            {appeal.latestActivity || '—'}
                          </AdminLinkAction>
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Current Outcome</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          <AdminLinkAction
                            type="button"
                            className="text-left"
                            onClick={() => setActiveTab('decision')}
                          >
                            {appeal.currentOutcome || 'Pending'}
                          </AdminLinkAction>
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Applied Rule</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          {appeal.appliedRuleId || '—'}
                          {appeal.appliedRule
                            ? ` · ${appeal.appliedRule.correctiveAction} · ${appeal.appliedRule.availabilityAtSubmission}`
                            : ''}
                        </AdminDetailFieldValue>
                      </div>
                    </div>

                    {/* Column 4 — assignment, dates, related */}
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Assigned Handler</AdminDetailFieldLabel>
                        <div className="flex flex-wrap items-center gap-2">
                          <AdminDetailFieldValue>
                            {handlerPath ? (
                              <AdminLinkAction
                                type="button"
                                className="text-left"
                                onClick={() => navigate(handlerPath)}
                              >
                                {handlerDisplay}
                              </AdminLinkAction>
                            ) : (
                              handlerDisplay
                            )}
                          </AdminDetailFieldValue>
                          {canReassign && (
                            <AdminLinkAction type="button" onClick={openReassignModal}>
                              Reassign
                            </AdminLinkAction>
                          )}
                        </div>
                      </div>
                      {canStartReview ? (
                        <div className="space-y-1">
                          <AdminHeaderAction type="button" onClick={handleStartReview}>
                            Start Review
                          </AdminHeaderAction>
                        </div>
                      ) : null}
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Appeal Deadline</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          {formatDateTime(appeal.appealDeadline, { withZone: true })}
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Submitted</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          {formatDateTime(appeal.submittedOn)}
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Updated</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          {formatDateTime(appeal.lastUpdated)}
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>
                          {appeal.accountType === 'Customer' ? 'Customer' : 'Business'}
                        </AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          <AdminLinkAction
                            type="button"
                            className="text-left"
                            onClick={() => navigate(accountPath)}
                          >
                            {appeal.accountId} — {appeal.submittedByName}
                          </AdminLinkAction>
                        </AdminDetailFieldValue>
                      </div>
                      {(appeal.affectedRecordKind === 'Post' ||
                        appeal.affectedRecordKind === 'Review') && (
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>
                            {appeal.affectedRecordKind === 'Post'
                              ? 'Affected Post'
                              : 'Affected Review'}
                          </AdminDetailFieldLabel>
                          <AdminDetailFieldValue>
                            {affectedRecordPath ? (
                              <AdminLinkAction
                                type="button"
                                className="text-left"
                                onClick={() => navigate(affectedRecordPath)}
                              >
                                {appeal.affectedRecordId} — {appeal.affectedRecordTitle}
                              </AdminLinkAction>
                            ) : (
                              `${appeal.affectedRecordId || '—'} — ${
                                appeal.affectedRecordTitle || '—'
                              }`
                            )}
                          </AdminDetailFieldValue>
                        </div>
                      )}
                      {appeal.relatedModerationCaseId && appeal.source === 'Moderation' ? (
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Moderation Case</AdminDetailFieldLabel>
                          {renderCopyableValue(
                            'relatedCaseId',
                            appeal.relatedModerationCaseId,
                            'Moderation Case',
                            <AdminLinkAction
                              type="button"
                              className="text-left"
                              onClick={() =>
                                navigate(
                                  `/admin/moderation/queue/${appeal.relatedModerationCaseId}`
                                )
                              }
                            >
                              {appeal.relatedModerationCaseId}
                            </AdminLinkAction>
                          )}
                        </div>
                      ) : null}
                      {appeal.relatedFraudCaseId ? (
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Fraud Case</AdminDetailFieldLabel>
                          <AdminDetailFieldValue>
                            <AdminLinkAction
                              type="button"
                              className="text-left"
                              onClick={() =>
                                navigate(`/admin/fraud/alerts/${appeal.relatedFraudCaseId}`)
                              }
                            >
                              {appeal.relatedFraudCaseId}
                            </AdminLinkAction>
                          </AdminDetailFieldValue>
                        </div>
                      ) : null}
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Source Record</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          <AdminLinkAction
                            type="button"
                            className="text-left"
                            onClick={() => navigate(sourceRecordPath)}
                          >
                            View Source Record
                          </AdminLinkAction>
                        </AdminDetailFieldValue>
                      </div>
                      {appeal.status === 'New' && isUnassigned ? (
                        <AdminSupportingText className="text-mediumgray">
                          Assign an eligible handler before Start Review and other handling
                          actions.
                        </AdminSupportingText>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'messages' && (
                <div>
                  <div
                    className="flex flex-col"
                    style={
                      canMessage && !isReadOnlyMessages
                        ? { height: 'calc(100vh - 512px)', minHeight: '400px' }
                        : {
                            height: 'calc(100vh - 360px)',
                            minHeight: '500px',
                            maxHeight: '800px',
                          }
                    }
                  >
                    <div
                      ref={messagesContainerRef}
                      className="flex-1 overflow-y-auto space-y-4 p-6 pr-2 slim-scrollbar"
                      style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#C0C0C0 #F2F2F2',
                      }}
                    >
                      {messagesChronological.length === 0 ? (
                        <div className="py-8 text-center">
                          <AdminSupportingText className="text-mediumgray">
                            No appeal messages yet.
                          </AdminSupportingText>
                        </div>
                      ) : (
                        messagesChronological.map((message) => {
                          const isHandler = Boolean(message.isHandler);
                          const messageTimestamp = message.timestamp
                            ? formatDateTime(message.timestamp)
                            : '—';
                          const avatarSrc = isHandler
                            ? null
                            : appeal.accountAvatar || 'https://i.pravatar.cc/100?img=1';

                          return (
                            <div
                              key={message.messageId}
                              className={`flex ${isHandler ? 'justify-start' : 'justify-end'}`}
                            >
                              <div
                                className={`flex max-w-[49%] ${
                                  isHandler ? 'flex-row' : 'flex-row-reverse'
                                } items-start gap-2`}
                              >
                                <div className="flex-shrink-0">
                                  {isHandler ? (
                                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center border border-lightgray ring-1 ring-offblack/5">
                                      <i className="fi fi-rs-headphones text-offblack text-sm" />
                                    </div>
                                  ) : (
                                    <img
                                      src={avatarSrc}
                                      alt=""
                                      className="w-8 h-8 rounded-full object-cover"
                                    />
                                  )}
                                </div>
                                <div className="flex flex-col min-w-0">
                                  <AdminMetadataText
                                    className={`mb-1 px-1 ${
                                      isHandler ? 'text-left' : 'text-right'
                                    } text-mediumgray`}
                                  >
                                    {message.senderName} — {message.senderType}
                                  </AdminMetadataText>
                                  <div
                                    className={`px-4 py-2 rounded-xl min-w-0 ${
                                      isHandler
                                        ? 'bg-primaryblue text-white rounded-tl-md'
                                        : 'bg-mistygray text-offblack rounded-tr-md'
                                    }`}
                                  >
                                    <AdminMainText
                                      className={`${
                                        isHandler ? 'text-white' : 'text-offblack'
                                      } leading-relaxed whitespace-pre-wrap`}
                                    >
                                      {message.message}
                                    </AdminMainText>
                                    {renderMessageAttachments(message.attachments, isHandler)}
                                  </div>
                                  <div
                                    className={`mt-1 px-2 ${
                                      isHandler ? 'text-left' : 'text-right'
                                    }`}
                                  >
                                    <AdminMetadataText className="text-mediumgray">
                                      {messageTimestamp}
                                    </AdminMetadataText>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {canMessage && !isReadOnlyMessages ? (
                    <div ref={replyComposerRef} className="p-5">
                      <AdminDetailFieldLabel className="mb-3 block">Message</AdminDetailFieldLabel>
                      <textarea
                        value={replyText}
                        onChange={handleReplyTextChange}
                        onBlur={() => {
                          if (!replyText.trim()) setReplyError('Message is required.');
                        }}
                        placeholder="Type your reply here..."
                        className={adminFormTextareaClasses(Boolean(replyError))}
                        rows={5}
                        aria-invalid={Boolean(replyError)}
                      />
                      {replyError ? (
                        <AdminAlertText className="mt-1.5">{replyError}</AdminAlertText>
                      ) : null}
                      <div className="mt-3 flex flex-wrap items-center gap-3">
                        <input
                          ref={replyFileInputRef}
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.txt"
                          onChange={handleReplyFileChange}
                          className="hidden"
                        />
                        <div className="relative" ref={composerMenuRef}>
                          <AdminRowActionsButton
                            className="!w-10 !h-10 border border-mediumgray bg-white hover:bg-pastalBlueWhite hover:border-primaryblue"
                            onClick={() => setComposerMenuOpen((open) => !open)}
                            title="More actions"
                            aria-label="More actions"
                            aria-expanded={composerMenuOpen}
                            aria-haspopup="menu"
                          >
                            <AdminRowMenuIcon />
                          </AdminRowActionsButton>
                          {composerMenuOpen && (
                            <div
                              role="menu"
                              className="absolute left-0 bottom-full mb-1 z-30 min-w-[200px] w-max max-w-[16rem] rounded-lg border border-lightgray bg-white py-2 shadow-lg"
                            >
                              <AdminRowMenuItem
                                onClick={() => {
                                  setComposerMenuOpen(false);
                                  replyFileInputRef.current?.click();
                                }}
                              >
                                Add Attachment
                              </AdminRowMenuItem>
                            </div>
                          )}
                        </div>
                        <AdminHeaderAction
                          type="button"
                          onClick={handleSendMessage}
                          disabled={replySendDisabled}
                        >
                          Send Message
                        </AdminHeaderAction>
                        {replyFile && (
                          <AdminMetadataText className="text-mediumgray flex items-center gap-2">
                            <i className="fi fi-rs-check-circle text-freshgreen" aria-hidden />
                            {replyFile.name}
                            <AdminLinkAction
                              type="button"
                              onClick={() => {
                                setReplyFile(null);
                                setReplyAttachmentError('');
                                if (replyFileInputRef.current) replyFileInputRef.current.value = '';
                              }}
                            >
                              Remove
                            </AdminLinkAction>
                          </AdminMetadataText>
                        )}
                      </div>
                      {replyAttachmentError ? (
                        <AdminAlertText className="mt-1.5">{replyAttachmentError}</AdminAlertText>
                      ) : null}
                    </div>
                  ) : (
                    <div className="px-5 pb-2">
                      <AdminSupportingText className="text-mediumgray">
                        Appeal Messages are read-only when the Appeal is Decided or Withdrawn.
                      </AdminSupportingText>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'decision' && (
                <div>
                  {currentDecision ? (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {renderField(
                          'Appeal Outcome',
                          <AdminDetailFieldValue>
                            {currentDecision.appealOutcome || '—'}
                          </AdminDetailFieldValue>
                        )}
                        {renderField(
                          'Decision Reason',
                          <AdminDetailFieldValue className="whitespace-pre-wrap">
                            {currentDecision.decisionReason || '—'}
                          </AdminDetailFieldValue>
                        )}
                        {renderField(
                          'Corrective Action',
                          <AdminDetailFieldValue>
                            {currentDecision.correctiveAction ||
                              appeal.correctiveActionLabel ||
                              '—'}
                          </AdminDetailFieldValue>
                        )}
                        {renderField(
                          'Action Owner',
                          <AdminDetailFieldValue>
                            {currentDecision.correctiveActionOwner || appeal.actionOwner ? (
                              <AdminLinkAction
                                type="button"
                                className="text-left"
                                onClick={() => navigate(actionOwnerPath)}
                              >
                                {currentDecision.correctiveActionOwner || appeal.actionOwner}
                              </AdminLinkAction>
                            ) : (
                              '—'
                            )}
                          </AdminDetailFieldValue>
                        )}
                        {renderField(
                          'Action Result',
                          <AdminDetailFieldValue>
                            {appeal.actionResult || currentDecision.actionResult || '—'}
                          </AdminDetailFieldValue>
                        )}
                        {renderField(
                          'Decided By',
                          <AdminDetailFieldValue>
                            {currentDecision.decidedBy || '—'}
                          </AdminDetailFieldValue>
                        )}
                        {renderField(
                          'Decided On',
                          <AdminDetailFieldValue>
                            {formatDateTime(currentDecision.decidedOn)}
                          </AdminDetailFieldValue>
                        )}
                        {renderField(
                          'Notification Status',
                          <AdminDetailFieldValue>
                            {currentDecision.notificationStatus || '—'}
                          </AdminDetailFieldValue>
                        )}
                        {renderField(
                          'Decision State',
                          <AdminDetailFieldValue>
                            {currentDecision.decisionState || '—'}
                          </AdminDetailFieldValue>
                        )}
                        {renderField(
                          'Internal Decision Note',
                          <AdminDetailFieldValue className="whitespace-pre-wrap">
                            {currentDecision.internalDecisionNote || '—'}
                          </AdminDetailFieldValue>
                        )}
                      </div>

                      {[...(appeal.decisionHistory || [])]
                        .filter((entry) => entry.decisionState !== 'Current')
                        .sort((a, b) => (b.version || 0) - (a.version || 0))
                        .map((entry) => (
                          <div
                            key={`${entry.version}-${entry.decisionState}`}
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 border-t border-lightgray pt-6"
                          >
                            {renderField(
                              'Decision Version',
                              <AdminDetailFieldValue>{entry.version}</AdminDetailFieldValue>
                            )}
                            {renderField(
                              'Appeal Outcome',
                              <AdminDetailFieldValue>
                                {entry.appealOutcome || '—'}
                              </AdminDetailFieldValue>
                            )}
                            {renderField(
                              'Decision Reason',
                              <AdminDetailFieldValue className="whitespace-pre-wrap">
                                {entry.decisionReason || '—'}
                              </AdminDetailFieldValue>
                            )}
                            {renderField(
                              'Corrective Action',
                              <AdminDetailFieldValue>
                                {entry.correctiveAction || '—'}
                              </AdminDetailFieldValue>
                            )}
                            {renderField(
                              'Action Result',
                              <AdminDetailFieldValue>
                                {entry.actionResult || '—'}
                              </AdminDetailFieldValue>
                            )}
                            {renderField(
                              'Decided By',
                              <AdminDetailFieldValue>
                                {entry.decidedBy || '—'}
                              </AdminDetailFieldValue>
                            )}
                            {renderField(
                              'Decided On',
                              <AdminDetailFieldValue>
                                {formatDateTime(entry.decidedOn)}
                              </AdminDetailFieldValue>
                            )}
                            {renderField(
                              'Decision State',
                              <AdminDetailFieldValue>
                                {entry.decisionState || '—'}
                              </AdminDetailFieldValue>
                            )}
                            {entry.correctionReason
                              ? renderField(
                                  'Correction Reason',
                                  <AdminDetailFieldValue className="whitespace-pre-wrap">
                                    {entry.correctionReason}
                                  </AdminDetailFieldValue>
                                )
                              : null}
                            {renderField(
                              'Internal Decision Note',
                              <AdminDetailFieldValue className="whitespace-pre-wrap">
                                {entry.internalDecisionNote || '—'}
                              </AdminDetailFieldValue>
                            )}
                          </div>
                        ))}
                    </div>
                  ) : (
                    <AdminSupportingText className="text-mediumgray">
                      {isWithdrawn
                        ? 'This Appeal was Withdrawn. No Appeal Outcome is recorded.'
                        : 'No Appeal Decision recorded.'}
                    </AdminSupportingText>
                  )}
                </div>
              )}
            </div>
          </DashboardMainContainer>
        </div>
      </div>

      {attachmentPreview && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 px-4 py-8"
          onClick={() => setAttachmentPreview(null)}
          role="presentation"
        >
          <div
            className="w-full max-w-[min(100vw-32px,896px)] max-h-[90vh] flex flex-col rounded-xl bg-white border border-lightgray shadow-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={attachmentPreview.attachment?.name || 'Attachment preview'}
          >
            <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-lightgray shrink-0">
              <AdminPanelTitle className="text-left flex-1 min-w-0 !mb-0 break-words normal-case">
                {attachmentPreview.attachment?.name || 'Attachment'}
              </AdminPanelTitle>
              <button
                type="button"
                onClick={() => setAttachmentPreview(null)}
                className="flex-shrink-0 text-base text-offblack hover:text-primaryblue p-1 rounded-lg hover:bg-pastalBlueWhite transition-colors"
                aria-label="Close preview"
              >
                <i className="fi fi-rs-cross flex items-center justify-center w-full h-full" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-5 bg-pastalBlueWhite">
              {attachmentPreview.kind === 'image' && attachmentPreview.url && (
                <div className="flex justify-center rounded-lg bg-white border border-lightgray overflow-hidden">
                  <img
                    src={attachmentPreview.url}
                    alt=""
                    className="max-h-[75vh] w-auto max-w-full object-contain"
                  />
                </div>
              )}
              {attachmentPreview.kind === 'pdf' && attachmentPreview.url && (
                <iframe
                  title={attachmentPreview.attachment?.name || 'PDF preview'}
                  src={attachmentPreview.url}
                  className="w-full min-h-[70vh] rounded-lg border border-lightgray bg-white"
                />
              )}
              {(attachmentPreview.kind === 'other' || !attachmentPreview.url) && (
                <div className="rounded-xl bg-white border border-lightgray p-6 text-center">
                  <AdminSupportingText className="mb-4">
                    {attachmentPreview.url
                      ? 'This file opens in a new tab. Use the button below when you’re ready.'
                      : 'No preview is available for this attachment in the sandbox.'}
                  </AdminSupportingText>
                  {attachmentPreview.url && (
                    <AdminRowAction
                      onClick={() =>
                        window.open(attachmentPreview.url, '_blank', 'noopener,noreferrer')
                      }
                    >
                      Open in new tab
                    </AdminRowAction>
                  )}
                </div>
              )}
              {attachmentPreview.url &&
                (attachmentPreview.kind === 'image' || attachmentPreview.kind === 'pdf') && (
                  <div className="flex flex-wrap justify-center gap-3 mt-6">
                    <AdminRowAction
                      onClick={() =>
                        window.open(attachmentPreview.url, '_blank', 'noopener,noreferrer')
                      }
                    >
                      Open in new tab
                    </AdminRowAction>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {reassignModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setReassignModalOpen(false)}
        >
          <div
            className={`bg-white rounded-xl p-6 ${POPUP_FORM_CONTENT_WIDTH_CLASS} mx-4 shadow-lg border border-lightgray`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <AdminPanelTitle className="text-offblack">Reassign</AdminPanelTitle>
              <button
                type="button"
                onClick={() => setReassignModalOpen(false)}
                className="text-offblack hover:text-primaryblue shrink-0 text-base"
                aria-label="Close"
              >
                <i className="fi fi-rr-cross dashboard-icon" />
              </button>
            </div>
            <AdminSupportingText className="text-mediumgray mb-3">
              Current handler: {handlerDisplay}
            </AdminSupportingText>
            <div>
              <AdminDetailFieldLabel className="mb-1.5">Assigned Handler</AdminDetailFieldLabel>
              <div className="relative w-full min-w-0">
                <select
                  value={reassignSelection}
                  onChange={(e) => setReassignSelection(e.target.value)}
                  className={adminWizardSelectClasses()}
                >
                  {APPEAL_HANDLER_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                <span
                  className="pointer-events-none absolute right-2.5 top-1/2 z-[5] flex h-3.5 w-3.5 shrink-0 -translate-y-1/2 items-center justify-center"
                  aria-hidden
                >
                  <i className="fi fi-rs-angle-down block !text-[14px] !leading-none text-offblack" />
                </span>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <AdminFormSecondaryAction type="button" onClick={() => setReassignModalOpen(false)}>
                Cancel
              </AdminFormSecondaryAction>
              <AdminHeaderAction onClick={saveReassign}>Confirm</AdminHeaderAction>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AppealDetail;
